import React, { useCallback, useState } from 'react';
import { View, ScrollView, TouchableOpacity, Linking, Alert, Platform, Text } from 'react-native';
import { Button, ActivityIndicator, Portal, Modal } from 'react-native-paper';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { useRoute, useNavigation, RouteProp, useFocusEffect } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { isAdminOrSuperAgentRole } from '../redux/slices/authSlice';
import { fetchClientById, updateClient } from '../redux/slices/clientsSlice';
import { fetchCommandesByClient, deleteCommande } from '../redux/slices/commandesSlice';
import { fetchBoitiers } from '../redux/slices/stockBoitierSlice';
import { fetchSims } from '../redux/slices/stockCarteSimSlice';
import { fetchModelesBoitier } from '../redux/slices/modeleBoitierSlice';
import CommandeCard from '../components/CommandeCard';
import { Avatar, Badge, Card, FilterChip, IconCircleButton, Screen, SectionHeader, TextInput } from '../components/ui';
import { RADIUS, TYPOGRAPHY } from '../assets/theme';
import { makeStyles, useAppTheme } from '../theme/ThemeContext';
import { useLayout } from '../theme/useLayout';
import { RootStackParamList } from '../navigation/AppNavigator';
import { StatutCommande, Vehicule } from '../types';
import api from '../services/api';

type RouteType = RouteProp<RootStackParamList, 'ProfilClient'>;
type NavType = StackNavigationProp<RootStackParamList, 'ProfilClient'>;
const ABONNEMENT_CODES = ['*BAS', '*AS', '*A'];

function formatDateIso(isoDate?: string): string | null {
  if (!isoDate) return null;
  const parts = isoDate.split('-');
  if (parts.length !== 3) return isoDate;
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
}

function formatDH(value: number): string {
  return `${value.toLocaleString('fr-FR')} DH`;
}

/** Soft danger banner used for form errors inside the modals. */
function InlineError({ message }: { message: string }) {
  const styles = useStyles();
  const { colors } = useAppTheme();
  return (
    <View style={styles.errorBox}>
      <MaterialCommunityIcons name="alert-circle-outline" size={18} color={colors.danger} />
      <Text style={styles.errorMsg}>{message}</Text>
    </View>
  );
}

export default function ProfilClient() {
  const route = useRoute<RouteType>();
  const navigation = useNavigation<NavType>();
  const dispatch = useAppDispatch();
  const styles = useStyles();
  const { colors } = useAppTheme();
  const { isWide, width } = useLayout();
  const { selected: client } = useAppSelector((s) => s.clients);
  const { list: commandes, loading: cmdLoading } = useAppSelector((s) => s.commandes);
  const { list: boitiersStock } = useAppSelector((s) => s.stockBoitier);
  const { list: simsStock } = useAppSelector((s) => s.stockSim);
  const { list: modelesBoitier } = useAppSelector((s) => s.modelesBoitier);
  const canManageAdminFeatures = useAppSelector((s) => isAdminOrSuperAgentRole(s.auth.role));
  const { clientId } = route.params;

  const [showVehiculeModal, setShowVehiculeModal] = useState(false);
  const [editingVehicule, setEditingVehicule] = useState<Vehicule | null>(null);

  // Client edit modal state
  const [showClientEditModal, setShowClientEditModal] = useState(false);
  const [editNom, setEditNom] = useState('');
  const [editType, setEditType] = useState<'PARTICULIER' | 'AGENCE'>('PARTICULIER');
  const [editTelephone, setEditTelephone] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editIce, setEditIce] = useState('');
  const [editNomResponsable, setEditNomResponsable] = useState('');
  const [editError, setEditError] = useState<string | null>(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [marque, setMarque] = useState('');
  const [modele, setModele] = useState('');
  const [plaque, setPlaque] = useState('');
  const [imei, setImei] = useState('');
  const [typeBoitier, setTypeBoitier] = useState('');
  const [numTelSim, setNumTelSim] = useState('');
  const [savingV, setSavingV] = useState(false);
  const [deletingV, setDeletingV] = useState(false);
  const [vehiculeError, setVehiculeError] = useState<string | null>(null);
  const [showMarqueSugg, setShowMarqueSugg] = useState(false);
  const [showModeleSugg, setShowModeleSugg] = useState(false);
  const [showImeiSugg, setShowImeiSugg] = useState(false);
  const [showTypeBoitierSugg, setShowTypeBoitierSugg] = useState(false);
  const [showSimSugg, setShowSimSugg] = useState(false);

  const boitiersDisponibles = boitiersStock.filter((s) => s.statut === 'DISPONIBLE');
  const simsDisponibles = simsStock.filter((s) => s.statut === 'DISPONIBLE');
  const marqueSugg = [...new Set((client?.vehicules ?? []).map((v) => v.marque).filter((m) => !!m && m.toLowerCase().includes(marque.toLowerCase())))];
  const modeleSugg = [...new Set((client?.vehicules ?? []).map((v) => v.modele).filter((m) => !!m && m.toLowerCase().includes(modele.toLowerCase())))];
  const imeiSugg = boitiersDisponibles.filter((b) => b.imei.includes(imei)).map((b) => b.imei);
  const typeBoitierSugg = [...new Set([
    ...modelesBoitier.map((m) => m.nom),
    ...boitiersStock.map((b) => b.modele ?? ''),
    ...(client?.vehicules ?? []).map((v) => v.typeBoitier ?? ''),
  ].filter((m) => !!m && m.toLowerCase().includes(typeBoitier.toLowerCase())))];
  const simSugg = simsDisponibles.filter((s) => s.numero.includes(numTelSim)).map((s) => s.numero);

  useFocusEffect(
    useCallback(() => {
      dispatch(fetchClientById(clientId));
      dispatch(fetchCommandesByClient(clientId));
    }, [dispatch, clientId])
  );

  const totalCommande = commandes.reduce((s, c) => s + Number(c.totalTTC), 0);
  const totalVerse = commandes.reduce((s, c) => {
    const verse = c.paiements.reduce((ps, p) => ps + Number(p.montant), 0);
    if (c.statut === 'PAYEE') {
      return s + Number(c.totalTTC);
    }
    return s + verse;
  }, 0);
  const resteAPayer = Math.max(0, totalCommande - totalVerse);
  const todayIso = new Date().toISOString().slice(0, 10);
  const renewLimitDate = new Date();
  renewLimitDate.setDate(renewLimitDate.getDate() + 7);
  const renewLimitIso = renewLimitDate.toISOString().slice(0, 10);
  const commandesTriees = [...commandes].sort((a, b) => {
    const verseA = a.paiements.reduce((s, p) => s + Number(p.montant), 0);
    const verseB = b.paiements.reduce((s, p) => s + Number(p.montant), 0);
    const isOverdueA = (a.statut === 'FACTURE' || a.statut === 'BON_COMMANDE')
      && !!a.dateEcheance && a.dateEcheance < todayIso && (Number(a.totalTTC) - verseA) > 0;
    const isOverdueB = (b.statut === 'FACTURE' || b.statut === 'BON_COMMANDE')
      && !!b.dateEcheance && b.dateEcheance < todayIso && (Number(b.totalTTC) - verseB) > 0;
    if (isOverdueA !== isOverdueB) return isOverdueA ? -1 : 1;
    return new Date(b.date).getTime() - new Date(a.date).getTime();
  });
  const vehiculesAvecAbonnement = commandes.reduce((set, commande) => {
    const contientAbonnement = commande.lignesCommande.some((ligne) => {
      const code = (ligne.produitService?.code ?? '').trim().toUpperCase();
      return ABONNEMENT_CODES.includes(code);
    });
    if (!contientAbonnement) {
      return set;
    }
    if (commande.vehicule?.id) {
      set.add(commande.vehicule.id);
    }
    (commande.vehiculesConcernes ?? []).forEach((v) => set.add(v.id));
    return set;
  }, new Set<number>());
  (client?.vehicules ?? []).forEach((v) => {
    if (v.statut === 'ACTIF' || v.dateExpirationAbonnement) {
      vehiculesAvecAbonnement.add(v.id);
    }
  });
  const vehiculesSansAbonnementList = (client?.vehicules ?? []).filter((v) => !vehiculesAvecAbonnement.has(v.id));
  const vehiculesExpires = (client?.vehicules ?? []).filter(
    (v) => !!v.dateExpirationAbonnement && v.dateExpirationAbonnement < todayIso
  );

  const handleWhatsApp = () => {
    if (!client?.telephone) return;
    const msg = encodeURIComponent(
      `Bonjour ${client.nomRaisonSociale}, votre solde est de ${resteAPayer.toLocaleString('fr-FR')} DH.`
    );
    Linking.openURL(`https://wa.me/${client.telephone}?text=${msg}`);
  };

  const openAddModal = () => {
    setEditingVehicule(null);
    setMarque(''); setModele(''); setPlaque(''); setImei(''); setTypeBoitier(''); setNumTelSim('');
    setVehiculeError(null);
    dispatch(fetchBoitiers());
    dispatch(fetchSims());
    dispatch(fetchModelesBoitier());
    setShowVehiculeModal(true);
  };

  const openEditModal = (v: Vehicule) => {
    setEditingVehicule(v);
    setMarque(v.marque ?? '');
    setModele(v.modele ?? '');
    setPlaque(v.immatriculation ?? '');
    setImei(v.imei ?? '');
    setTypeBoitier(v.typeBoitier ?? '');
    setNumTelSim(v.numTelSim ?? '');
    setVehiculeError(null);
    dispatch(fetchBoitiers());
    dispatch(fetchSims());
    dispatch(fetchModelesBoitier());
    setShowVehiculeModal(true);
  };

  const handleSaveVehicule = async () => {
    if (!marque.trim() || !plaque.trim()) return;
    const imeiValue = imei.trim();
    const simValue = numTelSim.trim();
    const typeBoitierValue = typeBoitier.trim();
    const boitierExisteDansStock = imeiValue
      ? boitiersStock.some((b) => b.imei === imeiValue)
      : false;

    setVehiculeError(null);
    if (!editingVehicule) {
      if (!imeiValue) {
        setVehiculeError("L'IMEI est obligatoire lors de l'ajout d'un véhicule.");
        return;
      }
      if (!simValue) {
        setVehiculeError("Le N° Tel SIM est obligatoire lors de l'ajout d'un véhicule.");
        return;
      }
    }
    if (imeiValue && imeiValue.length !== 15) {
      setVehiculeError("L'IMEI doit contenir exactement 15 chiffres.");
      return;
    }
    if (simValue && simValue.length !== 10) {
      setVehiculeError('Le N° Tel SIM doit contenir exactement 10 chiffres.');
      return;
    }
    if (imeiValue && !boitierExisteDansStock && !typeBoitierValue) {
      setVehiculeError('Type de boitier obligatoire quand l\'IMEI n\'existe pas en stock.');
      return;
    }

    // Validate IMEI not already AFFECTE on another vehicle
    if (imeiValue) {
      const boitierExistant = boitiersStock.find((b) => b.imei === imeiValue && b.statut === 'AFFECTE');
      if (boitierExistant && imeiValue !== editingVehicule?.imei) {
        setVehiculeError('Ce boitier (IMEI) est déjà affecté à un autre véhicule.');
        return;
      }
    }
    if (simValue) {
      const simExistant = simsStock.find((s) => s.numero === simValue && s.statut === 'AFFECTE');
      if (simExistant && simValue !== editingVehicule?.numTelSim) {
        setVehiculeError('Cette carte SIM est déjà affectée à un autre véhicule.');
        return;
      }
    }

    setSavingV(true);
    try {
      const payload = {
        marque: marque.trim(),
        modele: modele.trim(),
        immatriculation: plaque.trim(),
        imei: imeiValue,
        typeBoitier: typeBoitierValue || undefined,
        numTelSim: simValue,
        client: { id: clientId },
      };
      if (editingVehicule) {
        await api.put(`/api/vehicules/${editingVehicule.id}`, payload);
      } else {
        const { data: createdVehicule } = await api.post<Vehicule>('/api/vehicules', payload);
        dispatch(fetchCommandesByClient(clientId));
        dispatch(fetchClientById(clientId));
        setShowVehiculeModal(false);
        if (createdVehicule?.id) {
          navigation.navigate('Abonnement', { vehiculeId: createdVehicule.id });
        }
        return;
      }
      dispatch(fetchClientById(clientId));
      setShowVehiculeModal(false);
    } catch (e: any) {
      const msg = e?.response?.data?.erreur ?? (editingVehicule ? 'Impossible de modifier le véhicule.' : "Impossible d'ajouter le véhicule.");
      setVehiculeError(msg);
    } finally {
      setSavingV(false);
    }
  };

  const handleDeleteVehicule = () => {
    if (!editingVehicule) return;
    Alert.alert(
      'Confirmer la suppression',
      `Supprimer le véhicule ${editingVehicule.marque} ${editingVehicule.modele} (${editingVehicule.immatriculation}) ?`,
      [
        { text: 'Annuler', style: 'cancel' },
        {
          text: 'Supprimer', style: 'destructive', onPress: async () => {
            setDeletingV(true);
            try {
              await api.delete(`/api/vehicules/${editingVehicule.id}`);
              dispatch(fetchClientById(clientId));
              setShowVehiculeModal(false);
            } catch {
              Alert.alert('Erreur', 'Impossible de supprimer le véhicule.');
            } finally {
              setDeletingV(false);
            }
          },
        },
      ]
    );
  };

  const openClientEditModal = () => {
    if (!client) return;
    setEditNom(client.nomRaisonSociale);
    setEditType(client.type);
    setEditTelephone(client.telephone ?? '');
    setEditEmail(client.email ?? '');
    setEditIce(client.ice ?? '');
    setEditNomResponsable(client.nomResponsable ?? '');
    setEditError(null);
    setShowClientEditModal(true);
  };

  const handleSaveClientEdit = async () => {
    if (!editNom.trim()) { setEditError('Le nom est obligatoire'); return; }
    if (!editTelephone.trim()) { setEditError('Le téléphone est obligatoire'); return; }
    if (editType === 'AGENCE') {
      if (!editIce.trim()) { setEditError("L'ICE est obligatoire pour une agence"); return; }
      if (!/^\d{15}$/.test(editIce)) { setEditError("L'ICE doit contenir exactement 15 chiffres"); return; }
      if (!editNomResponsable.trim()) { setEditError('Le nom du responsable est obligatoire'); return; }
    }
    setEditError(null);
    setSavingEdit(true);
    try {
      await dispatch(updateClient({
        id: clientId,
        data: {
          nomRaisonSociale: editNom.trim(),
          type: editType,
          telephone: editTelephone.trim(),
          email: editEmail.trim() || undefined,
          ice: editIce.trim() || undefined,
          nomResponsable: editNomResponsable.trim() || undefined,
        },
      })).unwrap();
      dispatch(fetchClientById(clientId));
      setShowClientEditModal(false);
    } catch (e: any) {
      setEditError(e?.message ?? 'Impossible de modifier le client.');
    } finally {
      setSavingEdit(false);
    }
  };

  const confirmDeleteVehicule = (v: Vehicule) => {
    confirmAction(
      `Supprimer ${v.marque} ${v.modele} (${v.immatriculation}) ?`,
      async () => {
        try {
          await api.delete(`/api/vehicules/${v.id}`);
          dispatch(fetchClientById(clientId));
        } catch {
          Alert.alert('Erreur', 'Impossible de supprimer le véhicule.');
        }
      },
      'Supprimer le véhicule'
    );
  };

  const confirmAction = (message: string, onConfirm: () => void, title = 'Confirmer') => {
    if (Platform.OS === 'web') {
      const webGlobal = globalThis as unknown as { confirm?: (msg: string) => boolean };
      const confirmed = typeof webGlobal.confirm === 'function'
        ? webGlobal.confirm(message)
        : true;
      if (confirmed) onConfirm();
      return;
    }

    Alert.alert(title, message, [
      { text: 'Annuler', style: 'cancel' },
      { text: 'Supprimer', style: 'destructive', onPress: onConfirm },
    ]);
  };

  const confirmDeleteCommande = (id: number, statut: StatutCommande) => {
    const typeLabel = statut === 'FACTURE'
      ? 'la facture'
      : statut === 'BON_COMMANDE'
        ? 'le bon de commande'
      : statut === 'PAYEE'
        ? 'la commande payée'
        : 'le devis';
    confirmAction(
      `Confirmer la suppression de ${typeLabel} #${id} ?`,
      async () => {
        try {
          await dispatch(deleteCommande(id)).unwrap();
          dispatch(fetchCommandesByClient(clientId));
        } catch (e: any) {
          const msg = typeof e === 'string' ? e : 'Impossible de supprimer cette commande.';
          Alert.alert('Erreur', msg);
        }
      },
      'Supprimer la commande'
    );
  };

  if (!client) {
    return (
      <View style={styles.center}>
        <ActivityIndicator color={colors.primary} />
      </View>
    );
  }

  const vehiculeSaveDisabled =
    savingV
    || deletingV
    || !marque.trim()
    || !plaque.trim()
    || (!editingVehicule && (!imei.trim() || !numTelSim.trim()))
    || (!!imei.trim() && !boitiersStock.some((b) => b.imei === imei.trim()) && !typeBoitier.trim());

  const isAgence = client.type === 'AGENCE';
  const modalStyle = [styles.modal, { width: Math.min(560, width - 32) }];
  const actionBtnStyle = isWide ? undefined : styles.actionBtnRow;

  return (
    <Screen>
      <View style={isWide ? styles.layoutWide : null}>
        {/* Profile summary (left column on wide screens) */}
        <View style={isWide ? styles.aside : null}>
          {/* Hero */}
          <Card raised style={styles.block}>
            <View style={styles.heroTop}>
              <Avatar name={client.nomRaisonSociale} size={64} variant={isAgence ? 'brand' : 'ink'} />
              <View style={styles.heroInfo}>
                <Text style={styles.clientName}>{client.nomRaisonSociale}</Text>
                <Badge
                  label={isAgence ? 'Agence' : 'Particulier'}
                  tone={isAgence ? 'brand' : 'neutral'}
                  icon={isAgence ? 'domain' : 'account-outline'}
                />
              </View>
              {canManageAdminFeatures && (
                <IconCircleButton
                  icon="pencil-outline"
                  accessibilityLabel="Modifier le client"
                  onPress={openClientEditModal}
                />
              )}
            </View>
            {client.telephone || client.email ? (
              <View style={styles.contacts}>
                {client.telephone ? (
                  <View style={styles.contactRow}>
                    <MaterialCommunityIcons name="phone-outline" size={18} color={colors.textSecondary} />
                    <Text style={styles.contact} selectable>{client.telephone}</Text>
                  </View>
                ) : null}
                {client.email ? (
                  <View style={styles.contactRow}>
                    <MaterialCommunityIcons name="email-outline" size={18} color={colors.textSecondary} />
                    <Text style={styles.contact} numberOfLines={1} selectable>{client.email}</Text>
                  </View>
                ) : null}
              </View>
            ) : null}
          </Card>

          {/* Financial Summary */}
          <Card style={styles.block}>
            <Text style={styles.cardTitle}>Situation financière</Text>
            <View style={styles.kvRow}>
              <Text style={styles.kvLabel}>Total commandé</Text>
              <Text style={styles.kvValue}>{formatDH(totalCommande)}</Text>
            </View>
            <View style={styles.kvRow}>
              <Text style={styles.kvLabel}>Total versé</Text>
              <Text style={styles.kvValue}>{formatDH(totalVerse)}</Text>
            </View>
            <View style={[styles.kvRow, styles.kvTotal]}>
              <Text style={styles.kvTotalLabel}>Reste à payer</Text>
              <Text style={[styles.kvTotalValue, { color: resteAPayer > 0 ? colors.danger : colors.success }]}>
                {formatDH(resteAPayer)}
              </Text>
            </View>
          </Card>

          {/* Actions */}
          <View style={[styles.actions, isWide && styles.actionsColumn]}>
            <Button
              mode="contained"
              icon="plus"
              onPress={() => navigation.navigate('NouvelleCommande', { clientId })}
              style={actionBtnStyle}
              buttonColor={colors.primary}
              textColor={colors.onPrimary}
            >
              Nouvelle Commande
            </Button>
            {client.telephone ? (
              <Button
                mode="outlined"
                icon="whatsapp"
                onPress={handleWhatsApp}
                style={[actionBtnStyle, styles.outlinedBtn]}
                textColor={colors.textPrimary}
              >
                WhatsApp
              </Button>
            ) : null}
          </View>

          {(vehiculesSansAbonnementList.length > 0 || (client.type === 'AGENCE' && vehiculesExpires.length > 0)) ? (
            <View style={[styles.actions, isWide && styles.actionsColumn]}>
              {vehiculesSansAbonnementList.length > 0 ? (
                <Button
                  mode="contained"
                  icon="file-plus-outline"
                  onPress={() => navigation.navigate('Abonnement', {
                    clientId,
                    mode: 'INSTALLATION',
                    vehiculeIds: vehiculesSansAbonnementList.map((v) => v.id),
                    servicesPreselectionnes: ['*B', '*A'],
                  })}
                  style={actionBtnStyle}
                  buttonColor={colors.brand}
                  textColor={colors.onBrand}
                >
                  {client.type === 'PARTICULIER'
                    ? `Bon de commande d'installation (${vehiculesSansAbonnementList.length})`
                    : `Devis installation (${vehiculesSansAbonnementList.length})`}
                </Button>
              ) : null}
              {client.type === 'AGENCE' && vehiculesExpires.length > 0 ? (
                <Button
                  mode="outlined"
                  icon="file-refresh-outline"
                  onPress={() => navigation.navigate('Abonnement', {
                    clientId,
                    mode: 'RENOUVELLEMENT',
                    vehiculeIds: vehiculesExpires.map((v) => v.id),
                    servicesPreselectionnes: ['*A'],
                  })}
                  style={[actionBtnStyle, styles.warningOutlinedBtn]}
                  textColor={colors.warning}
                >
                  Devis renouvellement ({vehiculesExpires.length})
                </Button>
              ) : null}
            </View>
          ) : null}
        </View>

        {/* Details (right column on wide screens) */}
        <View style={isWide ? styles.main : null}>
          {/* Véhicules */}
          <View style={styles.section}>
            <SectionHeader
              title={`Véhicules (${client.vehicules?.length ?? 0})`}
              right={(
                <Button
                  mode="outlined"
                  compact
                  icon="plus"
                  onPress={openAddModal}
                  textColor={colors.textPrimary}
                  style={styles.outlinedBtn}
                >
                  Ajouter
                </Button>
              )}
            />
            {client.vehicules?.length === 0 ? (
              <Card style={styles.emptyCard}>
                <MaterialCommunityIcons name="car-off" size={22} color={colors.textMuted} />
                <Text style={styles.empty}>Aucun véhicule enregistré</Text>
              </Card>
            ) : client.vehicules ? (
              <Card flush>
                {client.vehicules.map((v: Vehicule, index: number) => {
                  const statut = v.statut ?? 'INACTIF';
                  const estInactif = statut === 'INACTIF';
                  const estARenouveler = !!v.dateExpirationAbonnement && v.dateExpirationAbonnement <= renewLimitIso;
                  const sansAbonnement = !vehiculesAvecAbonnement.has(v.id);
                  const expirationLabel = formatDateIso(v.dateExpirationAbonnement);

                  return (
                    <View key={v.id} style={[styles.vehiculeRow, index > 0 && styles.rowDivider]}>
                      <View style={styles.vehiculeIcon}>
                        <MaterialCommunityIcons name="car-outline" size={22} color={colors.textPrimary} />
                      </View>
                      <View style={styles.vehiculeBody}>
                        <View style={styles.vehiculeTitleRow}>
                          <Text style={styles.vehiculeName}>{v.marque} {v.modele}</Text>
                          {v.immatriculation ? (
                            <View style={styles.plate}>
                              <Text style={styles.plateText}>{v.immatriculation}</Text>
                            </View>
                          ) : null}
                        </View>
                        {estInactif || estARenouveler ? (
                          <View style={styles.badgesRow}>
                            {estInactif ? <Badge label="Inactif" tone="danger" dot /> : null}
                            {estARenouveler ? (
                              <Badge label="À renouveler" tone="warning" icon="calendar-clock-outline" />
                            ) : null}
                          </View>
                        ) : null}
                        {expirationLabel || v.imei || v.numTelSim ? (
                          <View style={styles.metaRow}>
                            {expirationLabel ? (
                              <Text style={styles.meta}>
                                <Text style={styles.metaLabel}>Expiration : </Text>{expirationLabel}
                              </Text>
                            ) : null}
                            {v.imei ? (
                              <Text style={styles.meta}>
                                <Text style={styles.metaLabel}>IMEI : </Text>{v.imei}
                              </Text>
                            ) : null}
                            {v.numTelSim ? (
                              <Text style={styles.meta}>
                                <Text style={styles.metaLabel}>SIM : </Text>{v.numTelSim}
                              </Text>
                            ) : null}
                          </View>
                        ) : null}
                        {sansAbonnement || estARenouveler ? (
                          <View style={styles.vehiculeActions}>
                            {sansAbonnement && (
                              <Button
                                mode="contained-tonal"
                                compact
                                icon="plus-circle-outline"
                                onPress={() => navigation.navigate('Abonnement', { vehiculeId: v.id })}
                                buttonColor={colors.brandSoft}
                                textColor={colors.brand}
                                labelStyle={styles.smallBtnLabel}
                              >
                                Ajouter l'abonnement
                              </Button>
                            )}
                            {estARenouveler && (
                              <Button
                                mode="contained-tonal"
                                compact
                                icon="refresh"
                                onPress={() => navigation.navigate('Abonnement', { vehiculeId: v.id })}
                                buttonColor={colors.warningSoft}
                                textColor={colors.warning}
                                labelStyle={styles.smallBtnLabel}
                              >
                                Renouveler l'abonnement
                              </Button>
                            )}
                          </View>
                        ) : null}
                      </View>
                      <View style={styles.rowActions}>
                        <IconCircleButton
                          icon="pencil-outline"
                          variant="ghost"
                          size={36}
                          accessibilityLabel="Modifier le véhicule"
                          onPress={() => openEditModal(v)}
                        />
                        {canManageAdminFeatures && (
                          <IconCircleButton
                            icon="delete-outline"
                            variant="ghost"
                            size={36}
                            color={colors.danger}
                            accessibilityLabel="Supprimer le véhicule"
                            onPress={() => confirmDeleteVehicule(v)}
                          />
                        )}
                      </View>
                    </View>
                  );
                })}
              </Card>
            ) : null}
          </View>

          {/* Commandes */}
          <View style={styles.section}>
            <SectionHeader title={`Commandes (${commandes.length})`} />
            {cmdLoading ? (
              <ActivityIndicator color={colors.primary} style={styles.sectionLoader} />
            ) : commandes.length === 0 ? (
              <Card style={styles.emptyCard}>
                <MaterialCommunityIcons name="file-document-outline" size={22} color={colors.textMuted} />
                <Text style={styles.empty}>Aucune commande</Text>
              </Card>
            ) : (
              commandesTriees.map((cmd) => (
                <CommandeCard
                  key={cmd.id}
                  commande={cmd}
                  showEcheanceDepasseeFlag
                  onPress={() => navigation.navigate('DetailCommande', { commandeId: cmd.id })}
                  onDeleteCommande={(cmd.statut === 'DEVIS'
                    || cmd.statut === 'PAYEE'
                    || ((cmd.statut === 'FACTURE' || cmd.statut === 'BON_COMMANDE') && canManageAdminFeatures))
                    ? () => confirmDeleteCommande(cmd.id, cmd.statut)
                    : undefined}
                />
              ))
            )}
          </View>
        </View>
      </View>

      {/* Client edit modal */}
      <Portal>
        <Modal visible={showClientEditModal} onDismiss={() => setShowClientEditModal(false)} contentContainerStyle={modalStyle}>
          <ScrollView showsVerticalScrollIndicator={false}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Modifier le Client</Text>
              <IconCircleButton
                icon="close"
                variant="ghost"
                size={36}
                accessibilityLabel="Fermer"
                onPress={() => setShowClientEditModal(false)}
              />
            </View>

            <TextInput
              label="Nom / Raison Sociale *"
              value={editNom}
              onChangeText={setEditNom}
              style={styles.input}
            />

            <Text style={styles.fieldLabel}>Type de client</Text>
            <View style={styles.typeRow}>
              {(['PARTICULIER', 'AGENCE'] as const).map((t) => (
                <FilterChip
                  key={t}
                  label={t === 'PARTICULIER' ? 'Particulier' : 'Agence'}
                  icon={t === 'PARTICULIER' ? 'account-outline' : 'domain'}
                  selected={editType === t}
                  onPress={() => setEditType(t)}
                  style={styles.typeChip}
                />
              ))}
            </View>

            <TextInput
              label="Téléphone *"
              value={editTelephone}
              onChangeText={setEditTelephone}
              style={styles.input}
              keyboardType="phone-pad"
            />
            <TextInput
              label="Email"
              value={editEmail}
              onChangeText={setEditEmail}
              style={styles.input}
              keyboardType="email-address"
              autoCapitalize="none"
            />

            {editType === 'AGENCE' && (
              <>
                <TextInput
                  label="ICE (15 chiffres) *"
                  value={editIce}
                  onChangeText={setEditIce}
                  style={styles.input}
                  keyboardType="numeric"
                  maxLength={15}
                />
                <TextInput
                  label="Nom du Responsable *"
                  value={editNomResponsable}
                  onChangeText={setEditNomResponsable}
                  style={styles.input}
                />
              </>
            )}

            {editError ? <InlineError message={editError} /> : null}

            <View style={styles.modalActions}>
              <Button mode="text" textColor={colors.textPrimary} onPress={() => setShowClientEditModal(false)}>
                Annuler
              </Button>
              <Button
                mode="contained"
                onPress={handleSaveClientEdit}
                loading={savingEdit}
                disabled={savingEdit || !editNom.trim()}
              >
                Enregistrer
              </Button>
            </View>
          </ScrollView>
        </Modal>
      </Portal>

      {/* Véhicule modal (add / edit) */}
      <Portal>
        <Modal visible={showVehiculeModal} onDismiss={() => setShowVehiculeModal(false)} contentContainerStyle={modalStyle}>
          <ScrollView showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled">
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>
                {editingVehicule ? 'Modifier le Véhicule' : 'Ajouter un Véhicule'}
              </Text>
              <IconCircleButton
                icon="close"
                variant="ghost"
                size={36}
                accessibilityLabel="Fermer"
                onPress={() => setShowVehiculeModal(false)}
              />
            </View>
            <TextInput
              label="Marque *"
              value={marque}
              onChangeText={(t) => { setMarque(t); setShowMarqueSugg(true); }}
              onFocus={() => setShowMarqueSugg(true)}
              onBlur={() => setTimeout(() => setShowMarqueSugg(false), 150)}
              style={styles.input}
            />
            {showMarqueSugg && marqueSugg.length > 0 && (
              <View style={styles.suggestionsList}>
                {marqueSugg.slice(0, 5).map((s) => (
                  <TouchableOpacity key={s} style={styles.suggestionItem} onPress={() => { setMarque(s); setShowMarqueSugg(false); }}>
                    <Text style={styles.suggestionText}>{s}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            )}

            <TextInput
              label="Modèle"
              value={modele}
              onChangeText={(t) => { setModele(t); setShowModeleSugg(true); }}
              onFocus={() => setShowModeleSugg(true)}
              onBlur={() => setTimeout(() => setShowModeleSugg(false), 150)}
              style={styles.input}
            />
            {showModeleSugg && modeleSugg.length > 0 && (
              <View style={styles.suggestionsList}>
                {modeleSugg.slice(0, 5).map((s) => (
                  <TouchableOpacity key={s} style={styles.suggestionItem} onPress={() => { setModele(s); setShowModeleSugg(false); }}>
                    <Text style={styles.suggestionText}>{s}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            )}

            <TextInput
              label="Plaque d'immatriculation *"
              value={plaque}
              onChangeText={setPlaque}
              style={styles.input}
              autoCapitalize="characters"
            />

            <TextInput
              label={editingVehicule ? 'IMEI / Boitier (15 chiffres)' : 'IMEI / Boitier (15 chiffres) *'}
              value={imei}
              onChangeText={(t) => {
                const nextImei = t.replace(/\D/g, '').slice(0, 15);
                setImei(nextImei);
                const boitierStock = boitiersStock.find((b) => b.imei === nextImei);
                if (boitierStock?.modele) {
                  setTypeBoitier(boitierStock.modele);
                }
                setShowImeiSugg(true);
              }}
              onFocus={() => setShowImeiSugg(true)}
              onBlur={() => setTimeout(() => setShowImeiSugg(false), 150)}
              style={styles.input}
              keyboardType="numeric"
              maxLength={15}
            />
            {showImeiSugg && imeiSugg.length > 0 && (
              <View style={styles.suggestionsList}>
                {imeiSugg.slice(0, 5).map((s) => (
                  <TouchableOpacity
                    key={s}
                    style={styles.suggestionItem}
                    onPressIn={() => {
                      setImei(s);
                      const boitierStock = boitiersStock.find((b) => b.imei === s);
                      if (boitierStock?.modele) {
                        setTypeBoitier(boitierStock.modele);
                      }
                      setShowImeiSugg(false);
                    }}
                  >
                    <Text style={styles.suggestionText}>{s}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            )}

            <TextInput
              label="Type de boitier"
              value={typeBoitier}
              onChangeText={(t) => { setTypeBoitier(t); setShowTypeBoitierSugg(true); }}
              onFocus={() => setShowTypeBoitierSugg(true)}
              onBlur={() => setTimeout(() => setShowTypeBoitierSugg(false), 150)}
              style={styles.input}
            />
            {showTypeBoitierSugg && typeBoitierSugg.length > 0 && (
              <View style={styles.suggestionsList}>
                {typeBoitierSugg.map((s) => (
                  <TouchableOpacity key={s} style={styles.suggestionItem} onPressIn={() => { setTypeBoitier(s); setShowTypeBoitierSugg(false); }}>
                    <Text style={styles.suggestionText}>{s}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            )}
            {!!imei.trim() && !boitiersStock.some((b) => b.imei === imei.trim()) && typeBoitier === '' && (
              <Text style={styles.helperMsg}>
                Boitier non trouvé en stock: sélectionnez le type de boitier.
              </Text>
            )}

            <TextInput
              label={editingVehicule ? 'N° Tel SIM (10 chiffres)' : 'N° Tel SIM (10 chiffres) *'}
              value={numTelSim}
              onChangeText={(t) => { setNumTelSim(t.replace(/\D/g, '').slice(0, 10)); setShowSimSugg(true); }}
              onFocus={() => setShowSimSugg(true)}
              onBlur={() => setTimeout(() => setShowSimSugg(false), 150)}
              style={styles.input}
              keyboardType="phone-pad"
              maxLength={10}
            />
            {showSimSugg && simSugg.length > 0 && (
              <View style={styles.suggestionsList}>
                {simSugg.slice(0, 5).map((s) => (
                  <TouchableOpacity key={s} style={styles.suggestionItem} onPressIn={() => { setNumTelSim(s); setShowSimSugg(false); }}>
                    <Text style={styles.suggestionText}>{s}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            )}

            {vehiculeError ? <InlineError message={vehiculeError} /> : null}

            <View style={styles.modalActions}>
              <Button mode="text" textColor={colors.textPrimary} onPress={() => setShowVehiculeModal(false)}>
                Annuler
              </Button>
              <Button
                mode="contained"
                onPress={handleSaveVehicule}
                loading={savingV}
                disabled={vehiculeSaveDisabled}
              >
                {editingVehicule ? 'Modifier' : 'Enregistrer'}
              </Button>
            </View>
          </ScrollView>
        </Modal>
      </Portal>
    </Screen>
  );
}

const useStyles = makeStyles((c) => ({
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: c.background },

  // Layout
  layoutWide: { flexDirection: 'row', alignItems: 'flex-start', gap: 32 },
  aside: { width: 380 },
  main: { flex: 1, minWidth: 0 },
  block: { marginBottom: 16 },

  // Hero
  heroTop: { flexDirection: 'row', alignItems: 'center', gap: 16 },
  heroInfo: { flex: 1, minWidth: 0, gap: 8 },
  clientName: { ...TYPOGRAPHY.title, color: c.textPrimary },
  contacts: {
    marginTop: 20,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: c.divider,
    gap: 10,
  },
  contactRow: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  contact: { ...TYPOGRAPHY.body, color: c.textPrimary, flexShrink: 1 },

  // Finance
  cardTitle: { ...TYPOGRAPHY.heading, color: c.textPrimary, marginBottom: 8 },
  kvRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    paddingVertical: 8,
  },
  kvLabel: { fontSize: 14, color: c.textSecondary },
  kvValue: { fontSize: 15, fontWeight: '600', color: c.textPrimary },
  kvTotal: { marginTop: 8, paddingTop: 16, borderTopWidth: 1, borderTopColor: c.divider },
  kvTotalLabel: { ...TYPOGRAPHY.bodyStrong, color: c.textPrimary },
  kvTotalValue: { fontSize: 18, fontWeight: '800' },

  // Actions
  actions: { flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginBottom: 16 },
  actionsColumn: { flexDirection: 'column', flexWrap: 'nowrap' },
  actionBtnRow: { flexGrow: 1, flexBasis: 140 },
  outlinedBtn: { borderColor: c.border },
  warningOutlinedBtn: { borderColor: c.warning },

  // Sections
  section: { marginTop: 8, marginBottom: 24 },
  sectionLoader: { marginTop: 16 },
  emptyCard: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  empty: { ...TYPOGRAPHY.body, color: c.textSecondary, flexShrink: 1 },

  // Véhicules
  vehiculeRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 14,
    paddingVertical: 16,
    paddingHorizontal: 16,
  },
  rowDivider: { borderTopWidth: 1, borderTopColor: c.divider },
  vehiculeIcon: {
    width: 44,
    height: 44,
    borderRadius: RADIUS.md,
    backgroundColor: c.surfaceAlt,
    alignItems: 'center',
    justifyContent: 'center',
  },
  vehiculeBody: { flex: 1, minWidth: 0 },
  vehiculeTitleRow: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 8 },
  vehiculeName: { ...TYPOGRAPHY.bodyStrong, color: c.textPrimary, flexShrink: 1 },
  plate: {
    borderWidth: 1,
    borderColor: c.border,
    borderRadius: 6,
    paddingHorizontal: 6,
    paddingVertical: 1,
    backgroundColor: c.surfaceAlt,
  },
  plateText: { fontSize: 12, fontWeight: '700', color: c.textPrimary, letterSpacing: 0.5 },
  badgesRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 8 },
  metaRow: { flexDirection: 'row', flexWrap: 'wrap', columnGap: 16, rowGap: 2, marginTop: 8 },
  meta: { fontSize: 13, color: c.textPrimary },
  metaLabel: { color: c.textSecondary },
  vehiculeActions: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 12 },
  smallBtnLabel: { fontSize: 13, fontWeight: '700' },
  rowActions: { flexDirection: 'row', alignItems: 'center', gap: 2 },

  // Modals
  modal: {
    backgroundColor: c.surface,
    borderRadius: RADIUS.xl,
    padding: 24,
    margin: 16,
    maxWidth: 560,
    alignSelf: 'center',
    maxHeight: '90%',
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 20,
  },
  modalTitle: { ...TYPOGRAPHY.title, color: c.textPrimary, flexShrink: 1 },
  input: { marginBottom: 12 },
  fieldLabel: { ...TYPOGRAPHY.label, color: c.textSecondary, marginBottom: 8 },
  typeRow: { flexDirection: 'row', gap: 8, marginBottom: 16 },
  typeChip: { flex: 1, justifyContent: 'center' },
  suggestionsList: {
    borderWidth: 1,
    borderColor: c.border,
    borderRadius: RADIUS.md,
    backgroundColor: c.surfaceRaised,
    marginTop: -6,
    marginBottom: 12,
    overflow: 'hidden',
  },
  suggestionItem: {
    paddingVertical: 12,
    paddingHorizontal: 14,
    borderBottomWidth: 1,
    borderBottomColor: c.divider,
  },
  suggestionText: { fontSize: 14, color: c.textPrimary },
  helperMsg: { fontSize: 12, color: c.warning, marginTop: -4, marginBottom: 10, marginLeft: 4 },
  modalActions: { flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'center', gap: 8, marginTop: 12 },
  errorBox: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    backgroundColor: c.dangerSoft,
    borderRadius: RADIUS.md,
    paddingVertical: 10,
    paddingHorizontal: 12,
    marginTop: 4,
    marginBottom: 8,
  },
  errorMsg: { flex: 1, color: c.danger, fontSize: 13, lineHeight: 18 },
}));
